import { Ban, CheckCircle2, Edit, Eye, Loader2, Plus } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../../config/api';
import { useAppState } from '../../context/AppStateContext';
import { Badge } from '../common/Badge';
import { PageHeader } from '../common/PageHeader';
import { ui } from '../../styles/ui';

const ROLE_TONE = {
  'Super Admin': 'danger',
  Admin: 'violet',
  'Field Officer': 'info',
  Dealer: 'success',
  User: 'neutral',
};

export const UsersManagement = () => {
  const navigate = useNavigate();
  const { users, loadUsers, apiCall, setUsers } = useAppState();
  const [searchTerm, setSearchTerm] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [togglingId, setTogglingId] = useState(null);

  // Refetch every time this tab is opened, not just once at login.
  useEffect(() => {
    loadUsers(undefined, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch = searchTerm === '' ||
        user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.role?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = userFilter === 'all' || user.role === userFilter;
      return matchesSearch && matchesFilter;
    });
  }, [users, searchTerm, userFilter]);

  const toggleStatus = async (user) => {
    if (!window.confirm(`${user.isActive ? 'Deactivate' : 'Reactivate'} ${user.firstName} ${user.lastName}?`)) return;
    setTogglingId(user._id);
    try {
      await apiCall(API_CONFIG.endpoints.toggleUserStatus(user._id), 'PUT', null, true);
      setUsers((prev) => prev.map((u) => (u._id === user._id ? { ...u, isActive: !u.isActive } : u)));
    } catch (err) {
      alert(err.message || 'Failed to update status');
    } finally {
      setTogglingId(null);
    }
  };

  const RowActions = ({ user, size = 'md' }) => {
    const cls = size === 'sm' ? 'p-1.5' : 'p-1.5';
    const icon = size === 'sm' ? 'h-4 w-4' : 'h-[18px] w-[18px]';
    return (
      <div className="flex items-center gap-1">
        <button onClick={() => navigate(`/admin/users/${user._id}`)} className={`${cls} rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors`} title="View Details">
          <Eye className={icon} />
        </button>
        <button onClick={() => navigate(`/admin/users/${user._id}?edit=1`)} className={`${cls} rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors`} title="Edit Role">
          <Edit className={icon} />
        </button>
        <button
          onClick={() => toggleStatus(user)}
          disabled={togglingId === user._id}
          className={`${cls} rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 ${user.isActive ? 'hover:text-red-600 dark:hover:text-red-400' : 'hover:text-green-600 dark:hover:text-green-400'}`}
          title={user.isActive ? 'Deactivate User' : 'Reactivate User'}
        >
          {togglingId === user._id ? <Loader2 className={`${icon} animate-spin`} /> : user.isActive ? <Ban className={icon} /> : <CheckCircle2 className={icon} />}
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Users Management" subtitle="Manage your company's users and their roles" />

      <div className="flex flex-col sm:flex-row gap-2.5">
        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={`flex-1 ${ui.input}`}
        />
        <select value={userFilter} onChange={(e) => setUserFilter(e.target.value)} className={ui.input + ' sm:w-44'}>
          <option value="all">All Roles</option>
          <option value="User">Users</option>
          <option value="Field Officer">Field Officers</option>
          <option value="Dealer">Dealers</option>
          <option value="Officer">Officers</option>
        </select>
        <button
          onClick={() => navigate('/admin/users/add-officer')}
          className="inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white transition-all duration-150 active:scale-[0.97] whitespace-nowrap"
        >
          <Plus className="h-4 w-4" />
          Add Officer
        </button>
      </div>

      {/* Desktop table */}
      <div className={`hidden md:block ${ui.card} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className={ui.tableHeadCell}>User Details</th>
                <th className={ui.tableHeadCell}>Role & Status</th>
                <th className={ui.tableHeadCell}>Contact Info</th>
                <th className={ui.tableHeadCell}>Wallet</th>
                <th className={`${ui.tableHeadCell} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
              {filteredUsers.map((user) => (
                <tr key={user._id} className={ui.tableRow}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-9 h-9 bg-violet-600 rounded-lg flex items-center justify-center mr-3 shrink-0">
                        <span className="text-white font-semibold text-sm">{user.firstName?.[0]}{user.lastName?.[0]}</span>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">{user.firstName} {user.lastName}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">ID: {user._id?.slice(-8)}</div>
                        <div className="text-xs text-gray-400 dark:text-gray-500">Joined {new Date(user.createdAt).toLocaleDateString()}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="space-y-1.5">
                      <Badge tone={ROLE_TONE[user.role] || 'neutral'}>{user.role}</Badge>
                      <div className="flex items-center space-x-1.5">
                        <div className={`w-1.5 h-1.5 rounded-full ${user.isActive ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span className={`text-xs font-medium ${user.isActive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{user.isActive ? 'Active' : 'Inactive'}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-300">{user.email}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{user.mobile}</div>
                    {user.accessCode && <div className="text-xs text-gray-400 dark:text-gray-500 font-mono">Code: {user.accessCode}</div>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">{user.wallet?.balance || 0} {user.wallet?.currency || 'USD'}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Wallet Balance</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <RowActions user={user} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-3">
        {filteredUsers.map((user) => (
          <div key={user._id} className={`${ui.card} p-3.5`}>
            <div className="flex items-center justify-between mb-2.5">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-violet-600 rounded-lg flex items-center justify-center mr-2.5 shrink-0">
                  <span className="text-white font-semibold text-xs">{user.firstName?.[0]}{user.lastName?.[0]}</span>
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">{user.firstName} {user.lastName}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{user.email}</div>
                </div>
              </div>
              <RowActions user={user} size="sm" />
            </div>
            <div className="flex items-center justify-between text-sm">
              <Badge tone={ROLE_TONE[user.role] || 'neutral'}>{user.role}</Badge>
              <span className={`text-xs font-medium ${user.isActive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{user.isActive ? 'Active' : 'Inactive'}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
